import * as React from "react";
import { DEFAULT_TENANT_ID, PublicTenantConfig, brandCssVariables, resolveBranding } from "@njoy/common";

import { tenantHeaders } from "../utils/tenant";

// Loads the site's tenant config once at boot and brands the page with it:
// colours (CSS variables), title, theme colour, icons and the install manifest.
// The last config is cached so a returning visitor sees their brand at once;
// public/index.html applies the cached look before React even loads.

const serverUrl = () => (process.env.REACT_APP_SERVER_URL || "").replace(/\/$/, "");
const CONFIG_KEY = "tenantConfig";
// Read by the inline script in public/index.html: keep the two in step.
const BOOT_KEY = "tenantBoot";
// First visit: how long to wait for the config before showing njoy's defaults.
const FIRST_LOAD_TIMEOUT_MS = 3000;

interface TenantState {
  config: PublicTenantConfig | null;
  loading: boolean;
}

const TenantContext = React.createContext<TenantState>({ config: null, loading: true });

const readCache = (): PublicTenantConfig | null => {
  try {
    const cached = JSON.parse(localStorage.getItem(CONFIG_KEY) || "null");
    return cached?.id ? cached : null;
  } catch {
    return null;
  }
};

const writeCache = (config: PublicTenantConfig, boot: Record<string, unknown>) => {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    localStorage.setItem(BOOT_KEY, JSON.stringify(boot));
  } catch {
    // Storage blocked (private mode): the brand still applies, just not before load.
  }
};

const setLink = (rel: string, href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
};

/** What the page needs to look like this tenant; also what index.html applies early. */
const bootLook = (config: PublicTenantConfig) => {
  const brand = resolveBranding(config.branding);
  const isDefault = config.id === DEFAULT_TENANT_ID;
  return {
    vars: brandCssVariables(config.branding),
    title: isDefault ? null : brand.appName,
    themeColor: brand.primaryColor,
    icon: brand.iconUrl || brand.logoUrl,
    manifest: isDefault ? null : `${serverUrl()}/api/tenant-manifest/${config.slug}`,
  };
};

const applyLook = (look: ReturnType<typeof bootLook>) => {
  const root = document.documentElement.style;
  // Clear what an earlier (cached) look set, in case the tenant has since unset a colour.
  Array.from(root)
    .filter((name) => name.startsWith("--brand-"))
    .forEach((name) => root.removeProperty(name));
  Object.entries(look.vars).forEach(([name, value]) => root.setProperty(name, value));
  if (look.title) document.title = look.title;
  if (look.themeColor) document.head.querySelector('meta[name="theme-color"]')?.setAttribute("content", look.themeColor);
  if (look.icon) {
    setLink("icon", look.icon);
    setLink("apple-touch-icon", look.icon);
  }
  if (look.manifest) setLink("manifest", look.manifest);
};

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = React.useState<TenantState>(() => ({ config: readCache(), loading: true }));

  React.useEffect(() => {
    const controller = new AbortController();
    const firstVisit = !state.config;
    const timer = firstVisit ? setTimeout(() => controller.abort(), FIRST_LOAD_TIMEOUT_MS) : undefined;

    fetch(`${serverUrl()}/api/tenant-config`, { credentials: "include", headers: tenantHeaders(), signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((config: PublicTenantConfig | null) => {
        if (config) {
          writeCache(config, bootLook(config));
          setState({ config, loading: false });
        } else {
          setState((prev) => ({ ...prev, loading: false }));
        }
      })
      .catch(() => setState((prev) => ({ ...prev, loading: false })))
      .finally(() => clearTimeout(timer));

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // Once per page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (state.config) applyLook(bootLook(state.config));
  }, [state.config]);

  // First visit: hold the page briefly rather than flash njoy's brand on a
  // tenant's site. Returning visitors render at once from the cache.
  if (!state.config && state.loading) return null;

  return <TenantContext.Provider value={state}>{children}</TenantContext.Provider>;
};

export const useTenantContext = () => React.useContext(TenantContext);
